import axios, { AxiosInstance } from 'axios';

import { handleAuthExpiration, isNativeWebView } from './webview-detection';

interface ExtendedAxiosInstance extends AxiosInstance {
  formatError: (error: any, defaultMessage?: string) => string;
}

const formatErrorMessage = (error: any, defaultMessage?: string): string => {
  const errors = error.response?.data?.errors;
  if (typeof errors === 'string') {
    return errors || defaultMessage || 'An error occurred.';
  }
  return (
    errors?.map((e: any) => e.detail || e)?.join('\n') ||
    defaultMessage ||
    'An error occurred.'
  );
};

const api = axios.create({}) as ExtendedAxiosInstance;
api.defaults.headers.common['WEB-CLIENT'] = 'react';

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && isNativeWebView()) {
      handleAuthExpiration();
    }
    return Promise.reject(error);
  }
);

api.formatError = formatErrorMessage;

export default api;

// Streaming utilities (SSE)
export type SSECallbacks = {
  onOpen?: () => void;
  onPing?: () => void;
  onDelta?: (text: string) => void;
  onComplete?: (data: any) => void;
  onMessage?: (data: any) => void;
  onError?: (message: string) => void;
};

export type SSEOptions = {
  withCredentials?: boolean;
  log?: boolean;
};

// Build URL with query params; objects/arrays are JSON.stringified
export function buildUrl(
  path: string,
  params?: Record<string, unknown>
): string {
  const url = new URL(path, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (typeof value === 'object') {
        url.searchParams.set(key, JSON.stringify(value));
      } else {
        url.searchParams.set(key, String(value));
      }
    });
  }
  return url.toString();
}

// Open an EventSource with standard AI events (ping, delta, complete, message)
export function openEventStream(
  path: string,
  params: Record<string, unknown>,
  {
    onOpen,
    onPing,
    onDelta,
    onComplete,
    onMessage,
    onError,
  }: SSECallbacks = {},
  { withCredentials = true, log = false }: SSEOptions = {}
) {
  const url = buildUrl(path, params);
  if (log) {
    // eslint-disable-next-line no-console
    console.log('[SSE] Open', url);
  }
  const es = new EventSource(url, { withCredentials } as any);

  es.onopen = () => {
    if (log) {
      // eslint-disable-next-line no-console
      console.log('[SSE] Opened');
    }
    onOpen?.();
  };

  es.addEventListener('ping', () => {
    if (log) {
      // eslint-disable-next-line no-console
      console.log('[SSE] ping');
    }
    onPing?.();
  });

  es.addEventListener('delta', (evt: MessageEvent) => {
    let text = '';
    try {
      const parsed = JSON.parse(evt.data);
      text =
        typeof (parsed as any)?.text === 'string'
          ? (parsed as any).text
          : evt.data;
    } catch {
      text = evt.data;
    }
    if (log) {
      // eslint-disable-next-line no-console
      console.log('[SSE] delta', text?.slice(0, 120));
    }
    onDelta?.(text);
  });

  es.addEventListener('complete', (evt: MessageEvent) => {
    try {
      const parsed = JSON.parse(evt.data);
      if (log) {
        // eslint-disable-next-line no-console
        console.log('[SSE] complete', parsed);
      }
      onComplete?.(parsed?.data ?? parsed);
    } catch (e: any) {
      onError?.(e?.message || 'Malformed complete payload');
    }
    es.close();
  });

  es.addEventListener('message', (evt: MessageEvent) => {
    try {
      const parsed = JSON.parse(evt.data);
      if (log) {
        // eslint-disable-next-line no-console
        console.log('[SSE] message', parsed);
      }
      onMessage?.(parsed);
    } catch {
      onMessage?.(evt.data);
    }
  });

  es.addEventListener('error', () => {
    if (log) {
      // eslint-disable-next-line no-console
      console.error('[SSE] error');
    }
    onError?.('SSE error');
    es.close();
  });

  return es;
}

// Higher-level streaming helper that handles the common "started" lifecycle
// and exposes a simpler callbacks object used by models.
export type StreamCallbacks = {
  onStart?: () => void;
  onDelta?: (text: string) => void;
  onComplete?: (data: any) => void;
  onError?: (message: string) => void;
  onOpen?: () => void; // optional
};

export function openAIStream(
  path: string,
  params: Record<string, unknown>,
  callbacks: StreamCallbacks = {},
  options: SSEOptions = {}
) {
  let started = false;
  return openEventStream(
    path,
    params,
    {
      onOpen: () => {
        callbacks.onOpen?.();
      },
      onPing: () => {
        if (!started) {
          started = true;
          callbacks.onStart?.();
        }
      },
      onDelta: (text) => {
        if (!started) {
          started = true;
          callbacks.onStart?.();
        }
        callbacks.onDelta?.(text);
      },
      onComplete: (data) => {
        callbacks.onComplete?.(data);
      },
      onMessage: (data) => {
        if ((data as any)?.success && (data as any)?.data) {
          callbacks.onComplete?.((data as any).data);
        }
      },
      onError: (msg) => {
        callbacks.onError?.(msg);
      },
    },
    options
  );
}
